import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from "@/hooks/use-toast";

interface PeerConnection {
  connection: RTCPeerConnection;
  stream?: MediaStream;
  lastHealthCheck?: number;
  reconnectAttempts?: number;
}

interface SignalData {
  type: 'offer' | 'answer' | 'ice-candidate';
  from: string;
  to: string;
  data: any;
}

interface ConnectionHealth {
  peerId: string;
  state: RTCPeerConnectionState;
  iceState: RTCIceConnectionState;
  lastCheck: number;
}

const MAX_RECONNECT_ATTEMPTS = 3;
const HEALTH_CHECK_INTERVAL = 5000; // 5 seconds
const CONNECTION_TIMEOUT = 30000; // 30 seconds

const PUBLIC_STUN_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' },
];

const DEFAULT_TURN_SERVERS = [
  {
    urls: 'turn:openrelay.metered.ca:80',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
  {
    urls: 'turn:openrelay.metered.ca:443',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
  {
    urls: 'turn:openrelay.metered.ca:443?transport=tcp',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
];

// Prefer production TURN servers when provided via environment variables
const ICE_SERVERS = {
  iceServers: [
    ...PUBLIC_STUN_SERVERS,
    ...(import.meta.env.VITE_TURN_URLS && import.meta.env.VITE_TURN_USERNAME && import.meta.env.VITE_TURN_CREDENTIAL
      ? import.meta.env.VITE_TURN_URLS.split(',').map(url => ({
          urls: url.trim(),
          username: import.meta.env.VITE_TURN_USERNAME,
          credential: import.meta.env.VITE_TURN_CREDENTIAL,
        }))
      : DEFAULT_TURN_SERVERS),
  ]
};

export function useWebRTC(meetingId: string | undefined, userId: string | undefined, localStream: MediaStream | null) {
  const [peerConnections, setPeerConnections] = useState<Map<string, PeerConnection>>(new Map());
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [signalingChannel, setSignalingChannel] = useState<RealtimeChannel | null>(null);
  const [connectionHealth, setConnectionHealth] = useState<Map<string, ConnectionHealth>>(new Map());
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const peerConnectionsRef = useRef<Map<string, PeerConnection>>(new Map());
  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastTrackSignatureRef = useRef<string>('');
  const localTracksKey = localStream
    ? localStream.getTracks().map(track => `${track.kind}:${track.id}`).join('|')
    : '';

  // Update refs when state changes
  useEffect(() => {
    peerConnectionsRef.current = peerConnections;
    remoteStreamsRef.current = remoteStreams;
  }, [peerConnections, remoteStreams]);

  // When local tracks change (camera → screen share or back), make sure peers receive the new tracks
  useEffect(() => {
    if (!localStream) return;

    const trackSignature = localStream.getTracks().map(track => `${track.kind}:${track.id}`).join('|');
    if (trackSignature === lastTrackSignatureRef.current) return;

    const currentTracks = localStream.getTracks();
    const trackByKind = new Map<'audio' | 'video', MediaStreamTrack>();
    currentTracks.forEach(track => {
      trackByKind.set(track.kind as 'audio' | 'video', track);
    });

    peerConnectionsRef.current.forEach(({ connection }) => {
      const senders = connection.getSenders();

      // Ensure removed tracks (like screen share ending) are cleaned up
      senders.forEach(sender => {
        const matchingTrack = trackByKind.get(sender.track?.kind as 'audio' | 'video');
        if (!matchingTrack) {
          try {
            connection.removeTrack(sender);
          } catch (error) {
            console.error('Failed to remove stale sender', error);
          }
        } else if (sender.track?.id !== matchingTrack.id) {
          sender.replaceTrack(matchingTrack);
        }
      });

      // Add any new tracks that don't have senders yet
      currentTracks.forEach(track => {
        const sender = senders.find(s => s.track?.kind === track.kind);
        if (!sender) {
          connection.addTrack(track, localStream);
        }
      });
    });

    lastTrackSignatureRef.current = trackSignature;
  }, [localStream, localTracksKey]);

  const createPeerConnection = useCallback((peerId: string) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    // Add local stream tracks to the connection
    if (localStream) {
      localStream.getTracks().forEach(track => {
        console.log(`Adding ${track.kind} track to peer ${peerId}, enabled: ${track.enabled}`);
        pc.addTrack(track, localStream);
      });
    } else {
      console.warn(`No local stream available when creating peer connection for ${peerId}`);
    }

    // Handle incoming tracks
    pc.ontrack = (event) => {
      const remoteStream = event.streams[0];
      
      setRemoteStreams(prev => {
        const newStreams = new Map(prev);
        newStreams.set(peerId, remoteStream);
        return newStreams;
      });
      
      remoteStreamsRef.current.set(peerId, remoteStream);
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && userId && meetingId) {
        supabase.channel(`webrtc-${meetingId}`).send({
          type: 'broadcast',
          event: 'signal',
          payload: {
            type: 'ice-candidate',
            from: userId,
            to: peerId,
            data: event.candidate.toJSON()
          }
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      // Update connection health
      updateConnectionHealth(peerId, pc);

      if (pc.iceConnectionState === 'disconnected') {
        handleDisconnection(peerId);
      } else if (pc.iceConnectionState === 'failed') {
        setConnectionError('Connection setup failed. Please retry your join.');
        handleConnectionFailure(peerId);
      } else if (pc.iceConnectionState === 'connected') {
        resetReconnectAttempts(peerId);
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        removePeerConnection(peerId);
      }
    };

    const peerConn: PeerConnection = { 
      connection: pc,
      lastHealthCheck: Date.now(),
      reconnectAttempts: 0
    };
    peerConnectionsRef.current.set(peerId, peerConn);
    setPeerConnections(new Map(peerConnectionsRef.current));
    
    return pc;
  }, [localStream, userId, meetingId]);

  const updateConnectionHealth = useCallback((peerId: string, pc: RTCPeerConnection) => {
    const health: ConnectionHealth = {
      peerId,
      state: pc.connectionState,
      iceState: pc.iceConnectionState,
      lastCheck: Date.now()
    };
    
    setConnectionHealth(prev => {
      const newHealth = new Map(prev);
      newHealth.set(peerId, health);
      return newHealth;
    });
  }, []);

  const resetReconnectAttempts = useCallback((peerId: string) => {
    const peerConn = peerConnectionsRef.current.get(peerId);
    if (peerConn) {
      peerConn.reconnectAttempts = 0;
      peerConn.lastHealthCheck = Date.now();
    }
  }, []);

  const handleDisconnection = useCallback((peerId: string) => {
    const peerConn = peerConnectionsRef.current.get(peerId);
    if (!peerConn) return;

    const attempts = peerConn.reconnectAttempts || 0;
    
    if (attempts < MAX_RECONNECT_ATTEMPTS) {
      console.log(`Attempting to reconnect to ${peerId} (attempt ${attempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
      peerConn.reconnectAttempts = attempts + 1;
      
      // Wait a bit before reconnecting (handled by connection state monitoring)
      console.log(`Will attempt reconnection for ${peerId}`);
    } else {
      console.log(`Max reconnection attempts reached for ${peerId}, removing connection`);
      toast({
        title: "Connection Lost",
        description: "Unable to reconnect to a participant",
        variant: "destructive",
      });
      removePeerConnection(peerId);
    }
  }, []);

  const handleConnectionFailure = useCallback((peerId: string) => {
    console.log(`Connection failed for ${peerId}`);
    toast({
      title: "Connection Failed",
      description: "Failed to establish connection with a participant",
      variant: "destructive",
    });
    removePeerConnection(peerId);
  }, []);

  const removePeerConnection = useCallback((peerId: string) => {
    console.log(`Removing peer connection for ${peerId}`);
    const peerConn = peerConnectionsRef.current.get(peerId);
    if (peerConn) {
      peerConn.connection.close();
      peerConnectionsRef.current.delete(peerId);
      setPeerConnections(new Map(peerConnectionsRef.current));
    }

    remoteStreamsRef.current.delete(peerId);
    setRemoteStreams(new Map(remoteStreamsRef.current));
  }, []);

  const createOffer = useCallback(async (peerId: string) => {
    if (!userId || !meetingId) return;

    const pc = createPeerConnection(peerId);
    
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      await supabase.channel(`webrtc-${meetingId}`).send({
        type: 'broadcast',
        event: 'signal',
        payload: {
          type: 'offer',
          from: userId,
          to: peerId,
          data: offer
        }
      });
    } catch (error) {
      console.error(`Error creating offer for ${peerId}:`, error);
      setConnectionError('We hit an issue while connecting.');
    }
  }, [userId, meetingId, createPeerConnection]);

  const handleOffer = useCallback(async (signal: SignalData) => {
    if (!userId || signal.to !== userId) return;

    const pc = createPeerConnection(signal.from);
    
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      await supabase.channel(`webrtc-${meetingId}`).send({
        type: 'broadcast',
        event: 'signal',
        payload: {
          type: 'answer',
          from: userId,
          to: signal.from,
          data: answer
        }
      });
    } catch (error) {
      console.error(`Error handling offer from ${signal.from}:`, error);
      setConnectionError('We hit an issue while connecting.');
    }
  }, [userId, meetingId, createPeerConnection]);

  const handleAnswer = useCallback(async (signal: SignalData) => {
    if (!userId || signal.to !== userId) return;

    const peerConn = peerConnectionsRef.current.get(signal.from);
    
    if (peerConn) {
      try {
        await peerConn.connection.setRemoteDescription(new RTCSessionDescription(signal.data));
      } catch (error) {
        console.error(`Error handling answer from ${signal.from}:`, error);
        setConnectionError('We hit an issue while connecting.');
      }
    }
  }, [userId]);

  const handleIceCandidate = useCallback(async (signal: SignalData) => {
    if (!userId || signal.to !== userId) return;

    const peerConn = peerConnectionsRef.current.get(signal.from);
    
    if (peerConn) {
      try {
        await peerConn.connection.addIceCandidate(new RTCIceCandidate(signal.data));
      } catch (error) {
        console.error(`Error adding ICE candidate from ${signal.from}:`, error);
        setConnectionError('We hit an issue while connecting.');
      }
    }
  }, [userId]);

  // Set up signaling channel
  useEffect(() => {
    if (!meetingId || !userId) return;

    const channel = supabase
      .channel(`webrtc-${meetingId}`)
      .on('broadcast', { event: 'signal' }, ({ payload }: { payload: SignalData }) => {
        switch (payload.type) {
          case 'offer':
            handleOffer(payload);
            break;
          case 'answer':
            handleAnswer(payload);
            break;
          case 'ice-candidate':
            handleIceCandidate(payload);
            break;
        }
      })
      .on('broadcast', { event: 'peer-joined' }, ({ payload }: { payload: { peerId: string } }) => {
        if (payload.peerId !== userId) {
          createOffer(payload.peerId);
        }
      })
      .on('broadcast', { event: 'peer-left' }, ({ payload }: { payload: { peerId: string } }) => {
        removePeerConnection(payload.peerId);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Announce presence
          channel.send({
            type: 'broadcast',
            event: 'peer-joined',
            payload: { peerId: userId }
          });
        }
      });

    setSignalingChannel(channel);

    return () => {
      channel.unsubscribe();
    };
  }, [meetingId, userId, handleOffer, handleAnswer, handleIceCandidate, createOffer, removePeerConnection]);

  // Health check monitoring
  useEffect(() => {
    if (!meetingId || !userId) return;

    healthCheckIntervalRef.current = setInterval(() => {
      const now = Date.now();
      
      peerConnectionsRef.current.forEach((peerConn, peerId) => {
        const timeSinceLastCheck = now - (peerConn.lastHealthCheck || 0);
        const pc = peerConn.connection;
        
        // Check if connection has been inactive for too long
        if (timeSinceLastCheck > CONNECTION_TIMEOUT) {
          handleDisconnection(peerId);
          return;
        }
        
        // Update health check timestamp
        peerConn.lastHealthCheck = now;
        
        // Update connection health state
        updateConnectionHealth(peerId, pc);
      });
    }, HEALTH_CHECK_INTERVAL);

    return () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
    };
  }, [meetingId, userId, handleDisconnection, updateConnectionHealth]);

  // Clean up all connections on unmount
  useEffect(() => {
    return () => {
      peerConnectionsRef.current.forEach((peerConn) => {
        peerConn.connection.close();
      });
      peerConnectionsRef.current.clear();
      remoteStreamsRef.current.clear();
      
      if (signalingChannel && userId) {
        signalingChannel.send({
          type: 'broadcast',
          event: 'peer-left',
          payload: { peerId: userId }
        });
      }
    };
  }, []);

  return {
    remoteStreams: Array.from(remoteStreams.entries()),
    peerConnections: Array.from(peerConnections.entries()),
    connectionHealth: Array.from(connectionHealth.entries()),
    connectionError,
    clearConnectionError: () => setConnectionError(null)
  };
}
